import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import type { ApolloCache, ObservableQuery, Reference, StoreObject } from '@apollo/client';
import { Device } from '../../lib/types';
import { useRescueContext } from '../../lib/RescueContext';
import { useToast } from '../../components/Toast/ToastContext';
import {
  RescueDeviceStatsData,
  rescueDeviceStatsQuery,
  RescueDevicesProgramData,
  rescueDevicesProgramQuery,
  RescueOrderShipmentsData,
  rescueOrderShipmentsQuery,
  rescueCreatePetFosterTransferMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
  rescueCancelPetAdoptionTransferMutation,
  rescueMarkAdoptionTransferNotRecoveredMutation,
  rescueMarkAdoptionTransferRecoveredMutation,
  rescueMarkAdoptionTransferStalledMutation,
  rescueSendSmsMutation,
  rescueUnassignedDeviceInventoryQuery,
  RescueUnassignedDeviceInventoryData,
} from '../../lib/rescueQueries';
import useRescueDevices from '../../hooks/useRescueDevices';
import useAllPendingAdoptions from '../../hooks/useAllPendingAdoptions';
import useClientPagination from '../../hooks/useClientPagination';
import useDrainAllPages from '../../hooks/useDrainAllPages';
import useAdoptedPetIds from '../../hooks/useAdoptedPetIds';
import { isTransferStalled } from '../../lib/utils';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import PortalTabs, { TabDef } from '../../components/PortalTabs/PortalTabs';
import StockTable from './StockTable';
import DeviceSectionTable from './DeviceSectionTable';
import DevicesInTransitTable from './DevicesInTransitTable';
import PendingTransfersTable from './PendingTransfersTable';
import StalledTransfersTable from './StalledTransfersTable';
import type { StalledSortKey, SortDir } from './StalledTransfersTable';
import MarkRecoveredModal from './MarkRecoveredModal';
import NotRecoveredModal from './NotRecoveredModal';
import CompletedTransfersTable from './CompletedTransfersTable';
import AddPetModal from './AddPetModal';
import AddDeviceToPetModal from './AddDeviceToPetModal';
import TransferToFosterModal from './TransferToFosterModal';
import RecallDeviceModal from './RecallDeviceModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import ResendEmailModal from './ResendEmailModal';
import CancelTransferModal, { CancellationReason } from './CancelTransferModal';
import WrongAdopterInfoModal from './WrongAdopterInfoModal';
import DeviceReturnedModal from './DeviceReturnedModal';
import DeviceKeptModal from './DeviceKeptModal';
import TestingCancelModal from './TestingCancelModal';
import SendSmsModal from './SendSmsModal';
import OrderDevicesModal from './OrderDevicesModal';
import DevicesEnrollModal from './DevicesEnrollModal';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import {
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackDeviceResendEmail,
  trackDeviceCancelTransfer,
  trackDeviceCancelTransferFailed,
  trackDeviceMarkedRecovered,
  trackDeviceMarkedNotRecovered,
  trackDeviceMarkedStalled,
  trackDeviceSmsSent,
  trackDevicesOrderClicked,
  trackChipOwnerAdded,
} from '../../analytics';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import styles from './Devices.module.scss';

/** Page size for the inventory drain loop. Server clamps to its own max. */
const INVENTORY_PAGE_SIZE = 100;

/** Default page size for the server-side paginated sections. */
const PAGE_SIZE = 10;

/**
 * A pending adoption transfer is considered "stalled" once it has been awaiting
 * adopter activation for at least this many days. Stalled transfers are split
 * out of the normal Pending Transfers section into their own section.
 */
const STALLED_TRANSFER_DAYS = 7;

/** Show completed adoptions from the last 90 days. */
const COMPLETED_SINCE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString();
})();

/** Top-level tabs. */
type DeviceTab = 'devices' | 'fosters' | 'adoptions';

/** Sub-filters for the Devices tab, narrowing which panels are shown. */
type DeviceFilter = 'all' | 'stock' | 'activated' | 'inTransit';

const DEVICE_FILTERS: { key: DeviceFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'activated', label: 'Activated' },
  { key: 'stock', label: 'Stock' },
  { key: 'inTransit', label: 'In Transit' },
];

/**
 * Removes a resolved pending adoption transfer from the drained
 * PENDING_ADOPTIONS cache by pet id, decrementing its totalCount. Used instead
 * of a refetch for recover / not-recovered / cancel: that list is drained across
 * many fetchMore pages, so a blanket refetch would collapse it back to a single
 * page until the drain re-runs (see pendingResolveOpts).
 */
function removePendingTransferFromCache(cache: ApolloCache<unknown>, petGraphqlId: string): void {
  cache.modify({
    fields: {
      rescueDevices(existing, { storeFieldName, readField }) {
        // rescueDevices is keyed by its `input` arg; only touch the pending drain.
        if (!existing || !storeFieldName.includes('PENDING_ADOPTIONS')) {
          return existing;
        }
        const current = existing as { items: readonly (StoreObject | Reference)[]; totalCount: number };
        const nextItems = current.items.filter((item) => {
          const pet = readField('pet', item) as Reference | StoreObject | undefined;
          return readField('id', pet) !== petGraphqlId;
        });
        const removed = current.items.length - nextItems.length;
        if (removed === 0) {
          return existing;
        }
        return { ...current, items: nextItems, totalCount: Math.max(0, current.totalCount - removed) };
      },
    },
  });
}

/**
 * Mutation options for resolving a pending adoption transfer (recover /
 * not-recovered / cancel). Refetches active queries so the summary cards and the
 * paginated sections refresh, but skips the PENDING_ADOPTIONS drain query — that
 * query accumulates every page via fetchMore, and a blanket refetch would drop
 * it back to a single page. The resolved transfer is removed from that query's
 * cache directly in `update` instead.
 *
 * The `rescueUnassignedDeviceInventory` drain is the other fetchMore-accumulated
 * query on this page, and Mark Recovered adds a device to it (the collar returns
 * to unassigned stock). It is intentionally NOT shielded here: unlike the pending
 * drain — where resolving is a removal we can apply surgically by pet id — this is
 * an insertion, and the recovered mutation returns only `transfer { id status }`,
 * not the module fields (battery/online/lastConnection) an inventory row needs. So
 * we let it refetch to surface the recovered device. That collapses the drain back
 * to a single page and re-drains, but it self-heals and only churns for rescues
 * with more than INVENTORY_PAGE_SIZE unassigned devices — the same case where a
 * single refetch couldn't avoid the collapse anyway (the server clamps page size).
 */
const pendingResolveOpts = {
  refetchQueries: 'active' as const,
  onQueryUpdated(observableQuery: ObservableQuery<unknown>) {
    const input = observableQuery.options.variables?.input as { filter?: string } | undefined;
    if (input?.filter === 'PENDING_ADOPTIONS') {
      // The drain query is the one `update` just edited in place (resolved
      // transfer removed from its cache). Return `undefined` — NOT `false` — so
      // we skip the refetch (a refetch would collapse the drain back to page 1)
      // WITHOUT suppressing the cache broadcast: Apollo calls onQueryUpdated for
      // this query precisely because our cache.modify dirtied it, and returning
      // `false` here would silence that broadcast, leaving the just-resolved row
      // stuck in the Stalled/Pending tables until an unrelated re-render.
      return undefined;
    }
    // Refetch every other active query (summary counts, other sections, and the
    // unassigned-inventory drain — see above for why that one is left to
    // refetch-collapse and re-drain).
    return true;
  },
  update(cache: ApolloCache<unknown>, _result: unknown, options: { variables?: { input?: { petId?: string } } }) {
    const petId = options.variables?.input?.petId;
    if (petId) {
      removePendingTransferFromCache(cache, petId);
    }
  },
};

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Devices() {
  const { orgName } = useRescueContext();
  const {
    data: enrollmentData,
    loading: enrollmentLoading,
    refetch: refetchEnrollment,
  } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery);
  const isEnrolled = enrollmentData?.rescueDevicesProgram.enrolled ?? false;
  const { showToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const urlSearch = new URLSearchParams(location.search).get('search') || '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [currentTab, setCurrentTab] = useState<DeviceTab>('devices');
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Sync local input when URL changes externally (e.g. navigating from dashboard)
  useEffect(() => {
    setSearchInput(urlSearch);
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      history.replace({ search: params.toString() });
    }, 300);
  }

  // ── Org name (from context — resolves chipReseller or RESCUE household) ──

  const resellerName = orgName;

  // ── Stats (unfiltered counts for summary cards) ──

  const { data: statsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { completedSince: COMPLETED_SINCE },
  });
  const stats = statsData?.rescueDeviceStats;

  // ── Outstanding shipments (Devices in Transit) ──

  const { data: shipmentsData } = useQuery<RescueOrderShipmentsData>(rescueOrderShipmentsQuery);
  const shipments = shipmentsData?.rescueOrderShipments ?? [];

  // ── Unassigned Device Inventory ───────────
  //
  // The `rescueUnassignedDeviceInventory` query returns only delivered devices
  // not attached to a pet — server-filtered. We still page through with
  // fetchMore so the Unassigned section can show the full count even when the
  // server clamps a single page. Search is applied server-side via input.query
  // so the Unassigned section honors the search bar like Rescue/Foster do.

  const inventoryInput = useMemo(
    () => (searchQuery ? { query: searchQuery, limit: INVENTORY_PAGE_SIZE } : { limit: INVENTORY_PAGE_SIZE }),
    [searchQuery],
  );

  const {
    data: inventoryData,
    loading: inventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
    fetchMore: fetchMoreInventory,
  } = useQuery<RescueUnassignedDeviceInventoryData>(rescueUnassignedDeviceInventoryQuery, {
    variables: { input: inventoryInput },
    notifyOnNetworkStatusChange: true,
  });

  // Drain remaining pages whenever the cached result still has more, so the
  // Unassigned section can show the full count even when the server clamps a
  // single page. Scalar selectors keep the drain effect from re-running on
  // unrelated Apollo cache writes.
  const inventoryHasMore = inventoryData?.rescueUnassignedDeviceInventory?.hasMore ?? false;
  const inventoryItemsLength = inventoryData?.rescueUnassignedDeviceInventory?.items.length ?? 0;
  const inventoryTotalCount = inventoryData?.rescueUnassignedDeviceInventory?.totalCount ?? 0;

  const mergeInventoryItems = useCallback(
    (
      previous: RescueUnassignedDeviceInventoryData,
      fetchMoreResult: RescueUnassignedDeviceInventoryData,
    ): RescueUnassignedDeviceInventoryData => ({
      rescueUnassignedDeviceInventory: {
        ...fetchMoreResult.rescueUnassignedDeviceInventory,
        items: [
          ...previous.rescueUnassignedDeviceInventory.items,
          ...fetchMoreResult.rescueUnassignedDeviceInventory.items,
        ],
      },
    }),
    [],
  );

  useDrainAllPages<RescueUnassignedDeviceInventoryData>({
    input: inventoryInput,
    hasMore: inventoryHasMore,
    itemsLength: inventoryItemsLength,
    totalCount: inventoryTotalCount,
    fetchMore: fetchMoreInventory,
    mergeItems: mergeInventoryItems,
  });

  // ── Server-side paginated sections ────────

  const rescue = useRescueDevices('IN_RESCUE', searchQuery, PAGE_SIZE, resellerName);
  const foster = useRescueDevices('IN_FOSTER', searchQuery, PAGE_SIZE, resellerName);
  const completed = useRescueDevices('COMPLETED_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, COMPLETED_SINCE);

  // ── Pending adoptions (fetch-all, then split by age) ──
  //
  // We fetch every pending adoption up front and partition it client-side into
  // recent transfers ("Pending Transfers") and those awaiting activation for
  // more than STALLED_TRANSFER_DAYS ("Stalled Transfers"). Each section then
  // paginates client-side so its counts stay accurate.
  const {
    devices: allPending,
    loading: pendingLoading,
    error: pendingError,
  } = useAllPendingAdoptions(searchQuery, resellerName);

  const { freshTransfers, stalledTransfers } = useMemo(() => {
    const fresh: Device[] = [];
    const stalled: Device[] = [];
    for (const d of allPending) {
      // A transfer is stalled if the rescue explicitly flagged it (stalledAt) or
      // it has aged past the automatic threshold.
      if (d.stalledAt || isTransferStalled(d.transferDate, STALLED_TRANSFER_DAYS)) {
        stalled.push(d);
      } else {
        fresh.push(d);
      }
    }
    return { freshTransfers: fresh, stalledTransfers: stalled };
  }, [allPending]);

  // Stalled section is sortable by clicking the Adopter / Pet / Stalled headers.
  // Sort the full list before paginating so it spans all pages, not just the
  // current one. Default: most-stalled (oldest transfer) first.
  const [stalledSort, setStalledSort] = useState<{ key: StalledSortKey; dir: SortDir }>({
    key: 'stalled',
    dir: 'desc',
  });

  const sortedStalled = useMemo(() => {
    const arr = [...stalledTransfers];
    const { key, dir } = stalledSort;
    const mult = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp: number;
      if (key === 'adopter') {
        cmp = (a.adopterName ?? '').localeCompare(b.adopterName ?? '');
      } else if (key === 'pet') {
        cmp = (a.petName ?? '').localeCompare(b.petName ?? '');
      } else {
        // "Stalled" = age since transfer. Older transfer → more stalled, so a
        // larger age sorts as "greater". age ∝ -transferDate, hence tb - ta.
        const ta = a.transferDate ? new Date(a.transferDate).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.transferDate ? new Date(b.transferDate).getTime() : Number.POSITIVE_INFINITY;
        cmp = tb - ta;
      }
      return cmp * mult;
    });
    return arr;
  }, [stalledTransfers, stalledSort]);

  const pendingPagination = useClientPagination(freshTransfers, PAGE_SIZE);
  const stalledPagination = useClientPagination(sortedStalled, PAGE_SIZE);

  // Jump both client-paginated sections back to page 1 whenever the search
  // changes. useClientPagination only clamps when the list shrinks, so without
  // this a user on page 3 would stay there and see a mid-list slice of the new
  // results instead of the top matches. (The server-paginated sections reset
  // via useRescueDevices.)
  const pendingResetPage = pendingPagination.resetPage;
  const stalledResetPage = stalledPagination.resetPage;
  useEffect(() => {
    pendingResetPage();
    stalledResetPage();
  }, [searchQuery, pendingResetPage, stalledResetPage]);

  // Toggle direction when re-clicking the active column; otherwise start a new
  // column ascending. Jump back to page 1 so the new top row is visible.
  function handleStalledSort(key: StalledSortKey) {
    setStalledSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    stalledPagination.resetPage();
  }

  // Show skeleton rows only on the very first load (no pending data yet).
  const pendingSkeletonCount = pendingLoading && allPending.length === 0 ? PAGE_SIZE : 0;

  // Pet IDs of fully-adopted-out pets — used to hide mutation actions on their
  // device rows. While loading, treat rescue-row pets as potentially adopted so
  // we don't briefly show buttons we're about to hide.
  const { adoptedPetIds, loading: adoptedLoading } = useAdoptedPetIds();

  // ── Unassigned devices ──
  //
  // The query is named `rescueUnassignedDeviceInventory` and only returns
  // devices not attached to a pet, so no client-side filter is needed.

  const unassignedDevices = useMemo(() => inventoryData?.rescueUnassignedDeviceInventory?.items ?? [], [inventoryData]);
  const unassignedCount = unassignedDevices.length;

  // Stock paginates client-side over the already-drained inventory (mirrors the
  // Pending/Stalled sections), and jumps back to page 1 when the search changes.
  const stockPagination = useClientPagination(unassignedDevices, PAGE_SIZE);
  const stockResetPage = stockPagination.resetPage;
  useEffect(() => {
    stockResetPage();
  }, [searchQuery, stockResetPage]);

  // ── Loading / Error states ────────────────

  // Only show the full-page loading spinner on initial page load (no search active).
  // During search, Apollo may briefly have empty data for new variables — we don't
  // want to unmount the search bar (which would steal focus from the input).
  const initialLoading =
    !searchQuery &&
    rescue.loading &&
    foster.loading &&
    pendingLoading &&
    rescue.devices.length === 0 &&
    foster.devices.length === 0 &&
    allPending.length === 0;
  const hasError = rescue.error && foster.error && pendingError && inventoryError;

  // ── T2A Stats ──────────────────────────

  const pendingCount = stats?.pendingAdoptionsCount ?? 0;
  const completedCount = stats?.completedAdoptionsCount ?? 0;
  const totalTransfers = pendingCount + completedCount;
  const showStats = totalTransfers > 0;

  const allTimeRate = totalTransfers > 0 ? Math.round((completedCount / totalTransfers) * 100) : 0;

  function rateColorClass(pct: number): string {
    if (pct >= 80) {
      return styles.summaryValueGreen;
    }
    if (pct >= 60) {
      return styles.summaryValueAmber;
    }
    return styles.summaryValueRed;
  }

  // ── GraphQL mutations ─────────────────────

  const mutationOpts = { refetchQueries: 'active' as const };

  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [recallFosterTransfer] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDevice] = useMutation(unassignDeviceMutation, mutationOpts);
  // These resolve a pending adoption transfer, so they maintain the drained
  // PENDING_ADOPTIONS cache directly rather than refetch-collapsing it.
  const [cancelAdoptionTransferMut] = useMutation(rescueCancelPetAdoptionTransferMutation, pendingResolveOpts);
  const [markNotRecoveredMut] = useMutation(rescueMarkAdoptionTransferNotRecoveredMutation, pendingResolveOpts);
  const [markRecoveredMut] = useMutation(rescueMarkAdoptionTransferRecoveredMutation, pendingResolveOpts);
  // Flagging as stalled keeps the transfer PENDING; the mutation returns the
  // updated transfer (id + stalledAt), which Apollo writes back to the same
  // normalized entity in the PENDING_ADOPTIONS drain. That re-runs the
  // fresh/stalled split so the row moves to Stalled — no refetch needed (a
  // refetch would collapse the drain, like the resolve mutations above).
  const [markStalledMut] = useMutation(rescueMarkAdoptionTransferStalledMutation);
  const [sendSmsMut] = useMutation(rescueSendSmsMutation);
  const transferPetWithChip = useTransferPetWithChipToAdopter(mutationOpts);

  /** Reset all sections to page 1 after a mutation. */
  function resetAllPages() {
    rescue.resetPage();
    foster.resetPage();
    pendingPagination.resetPage();
    stalledPagination.resetPage();
    completed.resetPage();
  }

  // ── Helper: find device across all sections ──

  function findDevice(deviceId: string): Device | undefined {
    return (
      rescue.devices.find((d) => d.id === deviceId) ??
      foster.devices.find((d) => d.id === deviceId) ??
      allPending.find((d) => d.id === deviceId) ??
      completed.devices.find((d) => d.id === deviceId)
    );
  }

  // ── Modal state ──────────────────────────

  const [addPetOpen, setAddPetOpen] = useState(false);
  const [addPetDevice, setAddPetDevice] = useState<Device | null>(null);
  const [fosterOpen, setFosterOpen] = useState(false);
  const [fosterDevice, setFosterDevice] = useState<Device | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDevice, setRecallDevice] = useState<Device | null>(null);
  const [adopterOpen, setAdopterOpen] = useState(false);
  const [adopterDevice, setAdopterDevice] = useState<Device | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendDevice, setResendDevice] = useState<Device | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDevice, setCancelDevice] = useState<Device | null>(null);
  const [reenterOpen, setReenterOpen] = useState(false);
  const [reenterDevice, setReenterDevice] = useState<Device | null>(null);
  const [returnedOpen, setReturnedOpen] = useState(false);
  const [returnedDevice, setReturnedDevice] = useState<Device | null>(null);
  const [keptOpen, setKeptOpen] = useState(false);
  const [keptDevice, setKeptDevice] = useState<Device | null>(null);
  const [testingOpen, setTestingOpen] = useState(false);
  const [testingDevice, setTestingDevice] = useState<Device | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);
  const [markRecoveredOpen, setMarkRecoveredOpen] = useState(false);
  const [markRecoveredDevice, setMarkRecoveredDevice] = useState<Device | null>(null);
  const [notRecoveredOpen, setNotRecoveredOpen] = useState(false);
  const [notRecoveredDevice, setNotRecoveredDevice] = useState<Device | null>(null);
  const [assignPetOpen, setAssignPetOpen] = useState(false);
  const [assignPetModuleId, setAssignPetModuleId] = useState<string | null>(null);
  const [orderDevicesOpen, setOrderDevicesOpen] = useState(false);
  // Modal opens by default for users who haven't enrolled in the devices
  // program. Once dismissed it stays closed for the rest of the page lifetime;
  // the empty state's CTA re-opens it.
  const [enrollModalDismissed, setEnrollModalDismissed] = useState(false);
  const enrollModalOpen = !enrollmentLoading && !isEnrolled && !enrollModalDismissed;
  // ── Modal handlers ──────────────────────────

  function handleOpenAddPet(device: Device) {
    setAddPetDevice(device);
    setAddPetOpen(true);
  }

  function handleOpenFoster(device: Device) {
    setFosterDevice(device);
    setFosterOpen(true);
  }

  function handleOpenRecall(device: Device) {
    setRecallDevice(device);
    setRecallOpen(true);
  }

  function handleOpenAdopter(device: Device) {
    setAdopterDevice(device);
    setAdopterOpen(true);
  }

  function handleOpenResend(device: Device) {
    setResendDevice(device);
    setResendOpen(true);
  }

  function handleOpenCancel(device: Device) {
    setCancelDevice(device);
    setCancelOpen(true);
  }

  function handleOpenSms(device: Device) {
    setSmsDevice(device);
    setSmsOpen(true);
  }

  function handleOpenAssignPet(moduleId: string) {
    setAssignPetModuleId(moduleId);
    setAssignPetOpen(true);
  }

  // ── Mutation handlers ──────────────────────────

  function handleAddPet(_deviceId: string, petName: string) {
    void petName;
    showToast(`Pet name update is not yet supported via the API`);
  }

  async function handleTransferToFoster(
    deviceId: string,
    firstName: string,
    lastName: string,
    fosterEmail: string,
    fosterPhone: string,
  ) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            email: fosterEmail,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: fosterPhone,
          },
        },
      });
      trackDeviceTransferToFoster('devices_page');
      resetAllPages();
      showToast(`${device.id} assigned to foster`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecall(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    if (device.holderType === 'foster') {
      // Foster recall — revoke the foster transfer
      try {
        await recallFosterTransfer({
          variables: {
            input: { petId: device._petGraphqlId },
          },
        });
        trackDeviceRecalled('devices_page');
        resetAllPages();
        showToast(`${device.id} recalled to rescue`);
      } catch (err) {
        showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } else {
      // Non-foster — just unassign the device from the pet
      try {
        await unassignDevice({
          variables: {
            moduleId: device.id,
            petId: device._petGraphqlId,
          },
        });
        resetAllPages();
        showToast(`Pet removed from ${device.id}`);
      } catch (err) {
        showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleTransferToAdopter(
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
  ) {
    const device = adopterDevice;
    if (!device) {
      return;
    }

    let result;
    try {
      result = await transferPetWithChip({
        petId: device._petGraphqlId,
        chipId: device.chip?.id ?? null,
        adopter: {
          firstName,
          lastName,
          email: adopterEmail,
          phone: adopterPhone,
          street: '',
          apt: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      trackDeviceTransferToAdopt('devices_page');
      resetAllPages();
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('devices_t2a');
      showToast(`Transfer initiated for ${device.id}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${device.id}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${device.id}`);
    }
  }

  // Each cancel reason resolves the pending adoption differently, so rather than
  // acting immediately we route to a per-reason confirmation modal that spells
  // out the outcome and performs the mutation once confirmed.
  function handleCancelAdoptionTransfer(device: Device, reason: CancellationReason) {
    switch (reason) {
      case 'wrong_adopter_info':
        // Cancel the transfer, then reopen the adopter screen prefilled.
        setReenterDevice(device);
        setReenterOpen(true);
        return;
      case 'device_returned':
        // Recover the device back to the rescue's unassigned stock.
        setReturnedDevice(device);
        setReturnedOpen(true);
        return;
      case 'device_kept':
        // Mark not recovered so it stays tracked in the Stalled table.
        setKeptDevice(device);
        setKeptOpen(true);
        return;
      case 'testing':
        // No real adoption — plain cancel returns the device to inventory.
        setTestingDevice(device);
        setTestingOpen(true);
        return;
    }
  }

  async function handleConfirmTestingCancel(device: Device) {
    try {
      // No real adoption — plain cancel returns the device to inventory.
      await cancelAdoptionTransferMut({ variables: { input: { petId: device._petGraphqlId } } });
      trackDeviceCancelTransfer('devices_page', 'testing');
      resetAllPages();
      showToast(`Adoption transfer cancelled for ${device.id}`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'testing');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so TestingCancelModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceKept(device: Device) {
    try {
      // Adopter kept the collar and the rescue doesn't have it (yet). Flag the
      // still-PENDING transfer as stalled so it moves to the Stalled Transfers
      // table for follow-up, rather than resolving it now.
      await markStalledMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated stalled event
      // captures the actual state transition (consistent with recovered / not-recovered).
      trackDeviceCancelTransfer('devices_page', 'device_kept');
      trackDeviceMarkedStalled('devices_page');
      resetAllPages();
      showToast(`${device.petName ?? device.id} moved to Stalled Transfers`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_kept');
      showToast(`Failed to flag as stalled: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceKeptModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceReturned(device: Device) {
    try {
      // Adopter keeps the pet but handed the collar back — recover it so it
      // returns to the rescue's unassigned stock.
      await markRecoveredMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated recovered event
      // keeps recovery counts consistent with the standalone Mark-Recovered flow
      // (same mutation, same signal).
      trackDeviceCancelTransfer('devices_page', 'device_returned');
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_returned');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceReturnedModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmReenterAdopter(device: Device) {
    const petId = device._petGraphqlId;
    try {
      // Bad adopter details — cancel the transfer (pet is kept) so we can reopen
      // the adoption screen prefilled and re-enter the correct info.
      await cancelAdoptionTransferMut({ variables: { input: { petId } } });
      trackDeviceCancelTransfer('devices_page', 'wrong_adopter_info');
      resetAllPages();
      showToast('Transfer cancelled — update the adopter details and resend');
      // Reopen the adopter transfer screen so the user can correct the info.
      handleOpenAdopter(device);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'wrong_adopter_info');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so WrongAdopterInfoModal stays open for retry.
      throw err;
    }
  }

  // ── Stalled transfer actions ──
  //
  // Send SMS reuses the existing SMS modal (handleOpenSms).

  function handleMarkRecovered(device: Device) {
    setMarkRecoveredDevice(device);
    setMarkRecoveredOpen(true);
  }

  async function handleConfirmMarkRecovered(device: Device) {
    try {
      // Closes the transfer and unassigns the device back to the rescue's stock,
      // recording a dedicated "recovered" audit event server-side.
      await markRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      showToast(`Failed to recover device: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so MarkRecoveredModal stays open for retry.
      throw err;
    }
  }

  function handleNotRecovered(device: Device) {
    setNotRecoveredDevice(device);
    setNotRecoveredOpen(true);
  }

  async function handleConfirmNotRecovered(device: Device) {
    try {
      // Steps the rescue out of the adoption household and marks the transfer
      // NOT_RECOVERED. The collar stays attached to its pet (so it doesn't
      // return to rescue stock) but is left claimable by a future owner.
      await markNotRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedNotRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} marked not recovered`);
    } catch (err) {
      showToast(`Failed to mark not recovered: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so NotRecoveredModal stays open for retry.
      throw err;
    }
  }

  async function handleSendSms(phoneNumber: string, message: string) {
    try {
      await sendSmsMut({ variables: { input: { phoneNumber, message } } });
      trackDeviceSmsSent();
      showToast('SMS sent successfully');
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleResendEmail(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    const to = device.adopterEmail ?? '';
    const subject = encodeURIComponent(`Set up your Fi collar${device.petName ? ` for ${device.petName}` : ''}`);
    const body = encodeURIComponent(
      `Hi${device.adopterName ? ` ${device.adopterName}` : ''},\n\nCongratulations on your adoption${
        device.petName ? ` of ${device.petName}` : ''
      }! Your new companion comes with a Fi GPS collar (${
        device.id
      }).\n\nTo activate the collar and start tracking, download the Fi app and follow the setup instructions:\nhttps://fitracking.com/app\n\nIf you have any questions, feel free to reach out to us or the Fi team at support@tryfi.com.\n\nBest,\n${orgName}`,
    );
    trackDeviceResendEmail();
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  // ── Order Devices ──────────────────────────

  function handleOrderDevices() {
    trackDevicesOrderClicked();
    setOrderDevicesOpen(true);
  }

  // ── Render ──────────────────────────

  if (enrollmentLoading) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner />
      </>
    );
  }

  if (!isEnrolled) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <EnrollEmptyState
          description="Not enrolled yet. Add GPS tracking to your rescue program to track fosters and reward adopters."
          ctaLabel="Set up now"
          onCta={() => setEnrollModalDismissed(false)}
        />
        <DevicesEnrollModal
          open={enrollModalOpen}
          onClose={() => setEnrollModalDismissed(true)}
          onEnrolled={() => {
            setEnrollModalDismissed(true);
            refetchEnrollment();
          }}
        />
      </>
    );
  }

  if (initialLoading) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner message="Loading devices..." />
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <div className={styles.errorContainer}>
          <p>Failed to load devices. Please try again.</p>
        </div>
      </>
    );
  }

  // ── Tabs & device sub-filter ──────────────
  const tabs: TabDef[] = [
    { key: 'devices', label: 'Devices', count: unassignedCount + rescue.totalCount },
    { key: 'fosters', label: 'Fosters', count: foster.totalCount },
    { key: 'adoptions', label: 'Adoptions', count: allPending.length + completed.totalCount },
  ];

  const showStock = deviceFilter === 'all' || deviceFilter === 'stock';
  const showActivated = deviceFilter === 'all' || deviceFilter === 'activated';
  const showInTransit = deviceFilter === 'all' || deviceFilter === 'inTransit';

  const adoptionsEmpty = freshTransfers.length === 0 && stalledTransfers.length === 0 && completed.totalCount === 0;

  return (
    <>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
        <div className={styles.headerActions}>
          <Button variant="green" onClick={handleOrderDevices}>
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Order Devices
          </Button>
        </div>
      </div>

      {/* T2A Summary Stats */}
      {showStats && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Pending</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueAmber}`}>{pendingCount}</div>
            <div className={styles.summaryCardSubtext}>awaiting adopter activation</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Transfer to Adoption Rate</div>
            <div className={`${styles.summaryCardValue} ${rateColorClass(allTimeRate)}`}>{allTimeRate}%</div>
            <div className={styles.summaryCardSubtext}>
              {completedCount} completed, {pendingCount} pending
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Fi Pets</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueGreen}`}>{completedCount}</div>
            <div className={styles.summaryCardSubtext}>adopted through your rescue</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <PortalTabs tabs={tabs} activeTab={currentTab} onTabChange={(key) => setCurrentTab(key as DeviceTab)} />

      {/* Search + device sub-filters (the filters apply to the Devices tab only) */}
      <div className={styles.searchFilterRow}>
        <div className={styles.searchFilterSearch}>
          <SearchBar
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search by device ID, pet name, or holder..."
          />
        </div>
        {currentTab === 'devices' && (
          <div className={styles.deviceFilters}>
            {DEVICE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.filterChip} ${deviceFilter === f.key ? styles.filterChipActive : ''}`}
                onClick={() => setDeviceFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Devices tab — Activated, Stock, In Transit */}
      {currentTab === 'devices' && (
        <>
          {showActivated && (
            <DeviceSectionTable
              title="Activated"
              description="Devices attached to pets currently in your rescue's household."
              emptyLabel="activated devices"
              showHolder={false}
              devices={rescue.devices}
              totalCount={rescue.totalCount}
              currentPage={rescue.currentPage}
              totalPages={rescue.totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={rescue.goToPage}
              skeletonCount={rescue.skeletonCount}
              searchQuery={searchQuery}
              adoptedPetIds={adoptedPetIds}
              adoptedLoading={adoptedLoading}
              onAddPet={handleOpenAddPet}
              onTransferFoster={handleOpenFoster}
              onRecall={handleOpenRecall}
              onTransferAdopter={handleOpenAdopter}
              onSendSms={handleOpenSms}
            />
          )}
          {showStock && (
            <StockTable
              devices={stockPagination.pageItems}
              totalCount={stockPagination.totalCount}
              currentPage={stockPagination.currentPage}
              totalPages={stockPagination.totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={stockPagination.goToPage}
              loading={inventoryLoading}
              hasError={!!inventoryError}
              onRetry={() => {
                refetchInventory();
              }}
              onAssign={handleOpenAssignPet}
              searchQuery={searchQuery}
            />
          )}
          {showInTransit && (
            <DevicesInTransitTable shipments={shipments} showWhenEmpty={deviceFilter === 'inTransit'} />
          )}
        </>
      )}

      {/* Fosters tab — Devices with a Foster */}
      {currentTab === 'fosters' && (
        <DeviceSectionTable
          title="Devices with a Foster"
          description="Devices attached to pets currently placed with a foster."
          emptyLabel="foster devices"
          devices={foster.devices}
          totalCount={foster.totalCount}
          currentPage={foster.currentPage}
          totalPages={foster.totalPages}
          pageSize={PAGE_SIZE}
          onPageChange={foster.goToPage}
          skeletonCount={foster.skeletonCount}
          searchQuery={searchQuery}
          adoptedPetIds={adoptedPetIds}
          adoptedLoading={adoptedLoading}
          onAddPet={handleOpenAddPet}
          onTransferFoster={handleOpenFoster}
          onRecall={handleOpenRecall}
          onTransferAdopter={handleOpenAdopter}
          onSendSms={handleOpenSms}
        />
      )}

      {/* Adoptions tab — Pending, Completed, Stalled */}
      {currentTab === 'adoptions' && (
        <>
          <PendingTransfersTable
            devices={pendingPagination.pageItems}
            totalCount={pendingPagination.totalCount}
            currentPage={pendingPagination.currentPage}
            totalPages={pendingPagination.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={pendingPagination.goToPage}
            loading={pendingLoading}
            skeletonCount={pendingSkeletonCount}
            onResendEmail={handleOpenResend}
            onCancelTransfer={handleOpenCancel}
            onSendSms={handleOpenSms}
          />
          <CompletedTransfersTable
            devices={completed.devices}
            totalCount={completed.totalCount}
            currentPage={completed.currentPage}
            totalPages={completed.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={completed.goToPage}
            loading={completed.loading}
            skeletonCount={completed.skeletonCount}
          />
          <StalledTransfersTable
            devices={stalledPagination.pageItems}
            totalCount={stalledPagination.totalCount}
            currentPage={stalledPagination.currentPage}
            totalPages={stalledPagination.totalPages}
            pageSize={PAGE_SIZE}
            thresholdDays={STALLED_TRANSFER_DAYS}
            onPageChange={stalledPagination.goToPage}
            skeletonCount={pendingSkeletonCount}
            sortKey={stalledSort.key}
            sortDir={stalledSort.dir}
            onSort={handleStalledSort}
            onMarkRecovered={handleMarkRecovered}
            onNotRecovered={handleNotRecovered}
            onSendSms={handleOpenSms}
          />
          {adoptionsEmpty && pendingSkeletonCount === 0 && completed.skeletonCount === 0 && (
            <div className={styles.tabEmptyState}>
              No adoptions to show{searchQuery ? ` for "${searchQuery}"` : ''}.
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <AddPetModal
        open={addPetOpen}
        onClose={() => setAddPetOpen(false)}
        device={addPetDevice}
        onAddPet={handleAddPet}
      />
      <TransferToFosterModal
        open={fosterOpen}
        onClose={() => setFosterOpen(false)}
        device={fosterDevice}
        onTransfer={handleTransferToFoster}
      />
      <RecallDeviceModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        device={recallDevice}
        onRecall={handleRecall}
      />
      <TransferToAdopterModal
        open={adopterOpen}
        onClose={() => {
          setAdopterOpen(false);
          setAdopterDevice(null);
        }}
        deviceId={adopterDevice?.id ?? ''}
        petName={adopterDevice?.petName ?? null}
        chipSerialNumber={adopterDevice?.chip?.serialNumber ?? null}
        showRescueDiscount
        initialFirstName={adopterDevice?.adopterFirstName ?? null}
        initialLastName={adopterDevice?.adopterLastName ?? null}
        initialEmail={adopterDevice?.adopterEmail ?? null}
        initialPhone={adopterDevice?.adopterPhone ?? null}
        onTransfer={handleTransferToAdopter}
      />
      <ResendEmailModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        device={resendDevice}
        onResend={handleResendEmail}
      />
      <CancelTransferModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelDevice(null);
        }}
        device={cancelDevice}
        onConfirm={handleCancelAdoptionTransfer}
      />
      <WrongAdopterInfoModal
        open={reenterOpen}
        onClose={() => {
          setReenterOpen(false);
          setReenterDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setReenterOpen(false);
          setCancelDevice(reenterDevice);
          setCancelOpen(true);
          setReenterDevice(null);
        }}
        device={reenterDevice}
        onConfirm={handleConfirmReenterAdopter}
      />
      <DeviceReturnedModal
        open={returnedOpen}
        onClose={() => {
          setReturnedOpen(false);
          setReturnedDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been recovered yet, so nothing is lost.
          setReturnedOpen(false);
          setCancelDevice(returnedDevice);
          setCancelOpen(true);
          setReturnedDevice(null);
        }}
        device={returnedDevice}
        onConfirm={handleConfirmDeviceReturned}
      />
      <DeviceKeptModal
        open={keptOpen}
        onClose={() => {
          setKeptOpen(false);
          setKeptDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been resolved yet, so nothing is lost.
          setKeptOpen(false);
          setCancelDevice(keptDevice);
          setCancelOpen(true);
          setKeptDevice(null);
        }}
        device={keptDevice}
        onConfirm={handleConfirmDeviceKept}
      />
      <TestingCancelModal
        open={testingOpen}
        onClose={() => {
          setTestingOpen(false);
          setTestingDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setTestingOpen(false);
          setCancelDevice(testingDevice);
          setCancelOpen(true);
          setTestingDevice(null);
        }}
        device={testingDevice}
        onConfirm={handleConfirmTestingCancel}
      />
      <SendSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        device={smsDevice}
        orgName={orgName}
        onSendSms={handleSendSms}
      />
      <MarkRecoveredModal
        open={markRecoveredOpen}
        onClose={() => {
          setMarkRecoveredOpen(false);
          setMarkRecoveredDevice(null);
        }}
        device={markRecoveredDevice}
        onConfirm={handleConfirmMarkRecovered}
      />
      <NotRecoveredModal
        open={notRecoveredOpen}
        onClose={() => {
          setNotRecoveredOpen(false);
          setNotRecoveredDevice(null);
        }}
        device={notRecoveredDevice}
        onConfirm={handleConfirmNotRecovered}
      />
      <AddDeviceToPetModal
        open={assignPetOpen}
        onClose={() => {
          setAssignPetOpen(false);
          setAssignPetModuleId(null);
        }}
        moduleId={assignPetModuleId}
      />
      <OrderDevicesModal open={orderDevicesOpen} onClose={() => setOrderDevicesOpen(false)} />
    </>
  );
}
